/**
 * Mycelium Event Backbone Service
 *
 * Central nervous system for FlowState Automate using mycelium protocol principles.
 * Routes events through the system enabling emergent intelligence from service interconnection.
 *
 * Key Principles:
 * - Minimal redundancy: Single event bus for all cross-service communication
 * - Maximum coherence: Events enriched with context as they flow
 * - Natural growth: Services subscribe to events they care about
 * - Self-organizing: No hard-coded service dependencies
 *
 * Infrastructure:
 * - Dead Letter Queue: Failed events are captured for later replay
 * - Circuit Breaker: Protects against cascading subscriber failures
 * - Rate Limiting: Per-subscriber throughput caps
 * - Event Versioning: Schema evolution support
 *
 * @module mycelium-event-backbone
 * @since 2026-02-19
 */
import type { IService, ServiceResponse } from "./service-registry";
import { createServiceError } from "./service-registry";
import { logger } from "../lib/logger";
// ============================================================================
// TYPES & INTERFACES
// ============================================================================
// Core event types are defined in @flowstate/mycelium-core (single source of truth).
// Re-exported here for backward compatibility with existing imports.
import type { EventDomain, MyceliumEvent, EventMetadata, DeadLetterEntry, } from "@flowstate/mycelium-core";
export type { EventDomain, MyceliumEvent, EventMetadata, DeadLetterEntry, };
// ============================================================================
// PRIORITY SCORING
// ============================================================================
/**
 * Compute a deterministic priority score for event queue ordering.
 *
 * Inspired by Orka's `priority_score(bucket × 10^15 + timestamp_µs)` pattern.
 * Lower score = processed first. Priority 10 (most important) maps to bucket 0;
 * priority 0 (least important) maps to bucket 10. Within the same bucket,
 * earlier timestamps produce lower scores (FIFO).
 *
 * @param priority - Event priority (0-10, higher = more important)
 * @param timestamp - Event timestamp in milliseconds since epoch
 * @returns A numeric score where lower = higher processing priority
 */
export function eventPriorityScore(priority: number, timestamp: number): number {
    const bucket = 10 - Math.max(0, Math.min(10, Math.round(priority)));
    return bucket * 1000000000000000 + timestamp;
}
/**
 * Event subscriber interface
 */
export interface EventSubscriber {
    /** Unique subscriber name */
    name: string;
    /** Event domains this subscriber cares about */
    domains?: EventDomain[];
    /** Specific event types to filter (within domains) */
    eventTypes?: string[];
    /** Handler function */
    handler: (event: MyceliumEvent) => Promise<void> | void;
    /** Priority for execution order (higher = earlier) */
    priority?: number;
    /** Maximum events per second (0 = unlimited) */
    maxEventsPerSecond?: number;
}
/**
 * Event enricher adds context as events flow
 */
export interface EventEnricher {
    /** Enricher name */
    name: string;
    /** Function to enrich event with additional context */
    enrich: (event: MyceliumEvent) => Promise<Partial<EventMetadata>> | Partial<EventMetadata>;
}
/**
 * Circuit breaker state for a subscriber
 */
interface SubscriberCircuitBreaker {
    state: "closed" | "open" | "half-open";
    consecutiveFailures: number;
    lastFailureAt: number;
    halfOpenSuccesses: number;
}
/**
 * Rate limiter state for a subscriber
 */
interface SubscriberRateLimiter {
    tokens: number;
    lastRefillAt: number;
}
/**
 * Statistics about event flow
 */
export interface EventBackboneStats {
    totalEventsPublished: number;
    totalEventsProcessed: number;
    eventsInFlight: number;
    subscriberCount: number;
    enricherCount: number;
    eventsByDomain: Record<EventDomain, number>;
    averageProcessingTime: number;
    errorCount: number;
    deadLetterCount: number;
    circuitBreakerTrips: number;
    rateLimitDrops: number;
}
// ============================================================================
// CONSTANTS
// ============================================================================
const CIRCUIT_BREAKER_FAILURE_THRESHOLD = 5;
const CIRCUIT_BREAKER_RESET_TIMEOUT_MS = 30000;
const CIRCUIT_BREAKER_HALF_OPEN_SUCCESS_THRESHOLD = 2;
const DLQ_MAX_SIZE = 1000;
// ============================================================================
// MYCELIUM EVENT BACKBONE SERVICE
// ============================================================================
/**
 * Central event backbone orchestrating service interconnections
 */
export class MyceliumEventBackbone implements IService {
    readonly name = "MyceliumEventBackbone";
    private subscribers: EventSubscriber[] = [];
    private enrichers: EventEnricher[] = [];
    private eventQueue: MyceliumEvent[] = [];
    private processing = false;
    private deferralTimerId: ReturnType<typeof setTimeout> | null = null;
    private deadLetterQueue: DeadLetterEntry[] = [];
    private circuitBreakers = new Map<string, SubscriberCircuitBreaker>();
    private rateLimiters = new Map<string, SubscriberRateLimiter>();
    private stats: EventBackboneStats = {
        totalEventsPublished: 0,
        totalEventsProcessed: 0,
        eventsInFlight: 0,
        subscriberCount: 0,
        enricherCount: 0,
        eventsByDomain: {} as Record<EventDomain, number>,
        averageProcessingTime: 0,
        errorCount: 0,
        deadLetterCount: 0,
        circuitBreakerTrips: 0,
        rateLimitDrops: 0,
    };
    async initialize(): Promise<void> {
        logger.info("🍄 Mycelium Event Backbone initializing - interconnecting services");
        this.startProcessing();
    }
    async dispose(): Promise<void> {
        this.processing = false;
        if (this.deferralTimerId != null) {
            clearTimeout(this.deferralTimerId);
            this.deferralTimerId = null;
        }
        logger.info("🍄 Mycelium Event Backbone disposed", {
            stats: this.stats,
        });
    }
    /**
     * Subscribe to events flowing through the mycelium
     */
    subscribe(subscriber: EventSubscriber): void {
        // Sort subscribers by priority (higher priority first)
        const priority = subscriber.priority ?? 5;
        const insertIndex = this.subscribers.findIndex(s => (s.priority ?? 5) < priority);
        if (insertIndex === -1) {
            this.subscribers.push(subscriber);
        }
        else {
            this.subscribers.splice(insertIndex, 0, subscriber);
        }
        this.stats.subscriberCount = this.subscribers.length;
        // Initialize circuit breaker for this subscriber
        this.circuitBreakers.set(subscriber.name, {
            state: "closed",
            consecutiveFailures: 0,
            lastFailureAt: 0,
            halfOpenSuccesses: 0,
        });
        // Initialize rate limiter if maxEventsPerSecond is set
        if (subscriber.maxEventsPerSecond && subscriber.maxEventsPerSecond > 0) {
            this.rateLimiters.set(subscriber.name, {
                tokens: subscriber.maxEventsPerSecond,
                lastRefillAt: Date.now(),
            });
        }
        logger.debug("🔗 Subscriber connected to mycelium", {
            subscriber: subscriber.name,
            domains: subscriber.domains,
            eventTypes: subscriber.eventTypes,
            priority,
            totalSubscribers: this.stats.subscriberCount,
        });
    }
    /**
     * Unsubscribe from event flow
     */
    unsubscribe(subscriberName: string): void {
        const index = this.subscribers.findIndex(s => s.name === subscriberName);
        if (index !== -1) {
            this.subscribers.splice(index, 1);
            this.stats.subscriberCount = this.subscribers.length;
            this.circuitBreakers.delete(subscriberName);
            this.rateLimiters.delete(subscriberName);
            logger.debug("🔌 Subscriber disconnected from mycelium", {
                subscriber: subscriberName,
            });
        }
    }
    /**
     * Register an enricher to add context to events
     */
    registerEnricher(enricher: EventEnricher): void {
        this.enrichers.push(enricher);
        this.stats.enricherCount = this.enrichers.length;
        logger.debug("✨ Enricher registered with mycelium", {
            enricher: enricher.name,
            totalEnrichers: this.stats.enricherCount,
        });
    }
    /**
     * Publish an event into the mycelium network.
     *
     * @param event - Event data (without id, timestamp, metadata)
     * @param options - Optional publish parameters
     * @param options.priority - Event priority (0-10, higher = more important). Default 5.
     * @param options.notBefore - Deferred processing timestamp (ms epoch). Event won't be processed until this time.
     */
    async publish(event: Omit<MyceliumEvent, "id" | "timestamp" | "metadata" | "version"> & {
        version?: 1;
    }, options?: {
        priority?: number;
        notBefore?: number;
    }): Promise<ServiceResponse<string>> {
        try {
            const priority = options?.priority ?? 5;
            const now = Date.now();
            // Create full event with metadata
            const fullEvent: MyceliumEvent = {
                ...event,
                id: this.generateEventId(),
                timestamp: now,
                version: event.version ?? 1,
                metadata: {
                    source: event.payload.source as string || "unknown",
                    processedBy: [],
                    context: {},
                    tags: [],
                    priority,
                    notBefore: options?.notBefore,
                },
            };
            // Priority-sorted insertion (lower score = processed first)
            // Inspired by Orka's composite score: bucket × 10^15 + timestamp
            const score = eventPriorityScore(priority, now);
            const insertIndex = this.eventQueue.findIndex(e => eventPriorityScore(e.metadata.priority, e.timestamp) > score);
            if (insertIndex === -1) {
                this.eventQueue.push(fullEvent);
            }
            else {
                this.eventQueue.splice(insertIndex, 0, fullEvent);
            }
            this.stats.totalEventsPublished++;
            this.stats.eventsInFlight++;
            // Track by domain
            this.stats.eventsByDomain[event.domain] =
                (this.stats.eventsByDomain[event.domain] || 0) + 1;
            logger.debug("📡 Event published to mycelium", {
                eventId: fullEvent.id,
                domain: fullEvent.domain,
                type: fullEvent.type,
                version: fullEvent.version,
                queueSize: this.eventQueue.length,
            });
            // Trigger processing if not already running
            if (!this.processing) {
                this.startProcessing();
            }
            return { success: true, data: fullEvent.id };
        }
        catch (error) {
            this.stats.errorCount++;
            const serviceError = error instanceof Error ? error : new Error("Unknown error");
            return { success: false, error: serviceError.message, code: "PUBLISH_FAILED", service: this.name };
        }
    }
    /**
     * Get current statistics
     */
    getStats(): EventBackboneStats {
        return { ...this.stats };
    }
    /**
     * Get all dead letter entries (failed events)
     */
    getDeadLetterQueue(): readonly DeadLetterEntry[] {
        return [...this.deadLetterQueue];
    }
    /**
     * Retry a dead letter entry by re-publishing the event
     */
    async retryDeadLetter(index: number): Promise<ServiceResponse<string>> {
        if (index < 0 || index >= this.deadLetterQueue.length) {
            return { success: false, error: `Dead letter index ${index} out of range`, code: "INVALID_INDEX", service: this.name };
        }
        const entry = this.deadLetterQueue.splice(index, 1)[0];
        this.stats.deadLetterCount = this.deadLetterQueue.length;
        // Re-publish the original event (strip backbone-assigned fields)
        const { id: _id, timestamp: _ts, metadata: _meta, ...eventPayload } = entry.event;
        return this.publish(eventPayload);
    }
    /**
     * Purge all dead letter entries
     */
    purgeDeadLetterQueue(): number {
        const count = this.deadLetterQueue.length;
        this.deadLetterQueue = [];
        this.stats.deadLetterCount = 0;
        return count;
    }
    /**
     * Get circuit breaker status for a subscriber
     */
    getCircuitBreakerState(subscriberName: string): SubscriberCircuitBreaker | undefined {
        return this.circuitBreakers.get(subscriberName);
    }
    /**
     * Manually reset a circuit breaker to closed state
     */
    resetCircuitBreaker(subscriberName: string): boolean {
        const breaker = this.circuitBreakers.get(subscriberName);
        if (!breaker)
            return false;
        breaker.state = "closed";
        breaker.consecutiveFailures = 0;
        breaker.halfOpenSuccesses = 0;
        return true;
    }
    // ============================================================================
    // PRIVATE METHODS
    // ============================================================================
    private generateEventId(): string {
        return `evt_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
    }
    private async startProcessing(): Promise<void> {
        if (this.processing)
            return;
        this.processing = true;
        let consecutiveDeferrals = 0;
        while (this.processing && this.eventQueue.length > 0) {
            // If all remaining events are deferred, schedule retry and break
            if (consecutiveDeferrals >= this.eventQueue.length) {
                const earliest = Math.min(...this.eventQueue
                    .filter(e => e.metadata.notBefore != null)
                    .map(e => e.metadata.notBefore!));
                const delay = Math.max(1, earliest - Date.now());
                if (this.deferralTimerId != null) {
                    clearTimeout(this.deferralTimerId);
                }
                this.deferralTimerId = setTimeout(() => {
                    this.deferralTimerId = null;
                    if (!this.processing) {
                        this.startProcessing();
                    }
                }, delay);
                break;
            }
            const event = this.eventQueue.shift();
            if (!event)
                continue;
            // Check deferred processing — re-enqueue if event is not yet mature
            if (event.metadata.notBefore && Date.now() < event.metadata.notBefore) {
                const score = eventPriorityScore(event.metadata.priority, event.timestamp);
                const insertIndex = this.eventQueue.findIndex(e => eventPriorityScore(e.metadata.priority, e.timestamp) > score);
                if (insertIndex === -1) {
                    this.eventQueue.push(event);
                }
                else {
                    this.eventQueue.splice(insertIndex, 0, event);
                }
                consecutiveDeferrals++;
                logger.debug("⏳ Event deferred (notBefore not reached)", {
                    eventId: event.id,
                    notBefore: event.metadata.notBefore,
                    now: Date.now(),
                });
                continue;
            }
            consecutiveDeferrals = 0;
            await this.processEvent(event);
        }
        this.processing = false;
    }
    private async processEvent(event: MyceliumEvent): Promise<void> {
        const startTime = Date.now();
        try {
            // Step 1: Enrich event with context
            await this.enrichEvent(event);
            // Step 2: Route to relevant subscribers
            await this.routeToSubscribers(event);
            // Step 3: Update statistics
            this.stats.totalEventsProcessed++;
            this.stats.eventsInFlight--;
            const processingTime = Date.now() - startTime;
            this.updateAverageProcessingTime(processingTime);
            logger.debug("✅ Event processed through mycelium", {
                eventId: event.id,
                domain: event.domain,
                type: event.type,
                processedBy: event.metadata.processedBy,
                processingTime,
            });
        }
        catch (error) {
            this.stats.errorCount++;
            this.stats.eventsInFlight--;
            logger.error("❌ Error processing event through mycelium", {
                eventId: event.id,
                domain: event.domain,
                type: event.type,
                error: error instanceof Error ? error.message : "Unknown error",
            });
        }
    }
    private async enrichEvent(event: MyceliumEvent): Promise<void> {
        for (const enricher of this.enrichers) {
            try {
                const enrichment = await enricher.enrich(event);
                // Merge enrichment into event metadata
                if (enrichment.context) {
                    event.metadata.context = {
                        ...event.metadata.context,
                        ...enrichment.context,
                    };
                }
                if (enrichment.tags) {
                    event.metadata.tags = [...event.metadata.tags, ...enrichment.tags];
                }
                if (enrichment.priority !== undefined) {
                    event.metadata.priority = Math.max(event.metadata.priority, enrichment.priority);
                }
            }
            catch (error) {
                logger.warn("Enricher failed, continuing", {
                    enricher: enricher.name,
                    eventId: event.id,
                    error: error instanceof Error ? error.message : "Unknown error",
                });
            }
        }
    }
    private async routeToSubscribers(event: MyceliumEvent): Promise<void> {
        // Find relevant subscribers
        const relevantSubscribers = this.subscribers.filter(subscriber => {
            // Check domain filter
            if (subscriber.domains && !subscriber.domains.includes(event.domain)) {
                return false;
            }
            // Check event type filter
            if (subscriber.eventTypes && !subscriber.eventTypes.includes(event.type)) {
                return false;
            }
            return true;
        });
        // Execute handlers (already sorted by priority)
        const handlerPromises = relevantSubscribers.map(async (subscriber) => {
            // Check circuit breaker
            if (!this.checkCircuitBreaker(subscriber.name)) {
                logger.debug("⚡ Circuit breaker open, skipping subscriber", {
                    subscriber: subscriber.name,
                    eventId: event.id,
                });
                return;
            }
            // Check rate limiter
            if (!this.checkRateLimit(subscriber.name)) {
                this.stats.rateLimitDrops++;
                logger.debug("🚦 Rate limit exceeded, skipping subscriber", {
                    subscriber: subscriber.name,
                    eventId: event.id,
                });
                return;
            }
            try {
                await subscriber.handler(event);
                event.metadata.processedBy.push(subscriber.name);
                this.recordCircuitBreakerSuccess(subscriber.name);
            }
            catch (error) {
                const errorMessage = error instanceof Error ? error.message : "Unknown error";
                logger.error("Subscriber handler failed", {
                    subscriber: subscriber.name,
                    eventId: event.id,
                    error: errorMessage,
                });
                this.recordCircuitBreakerFailure(subscriber.name);
                this.addToDeadLetterQueue(event, errorMessage, subscriber.name);
            }
        });
        await Promise.all(handlerPromises);
    }
    // ── Circuit Breaker ──────────────────────────────────────────────────
    private checkCircuitBreaker(subscriberName: string): boolean {
        const breaker = this.circuitBreakers.get(subscriberName);
        if (!breaker)
            return true; // No breaker = allow
        switch (breaker.state) {
            case "closed":
                return true;
            case "open": {
                // Check if enough time has passed to transition to half-open
                const elapsed = Date.now() - breaker.lastFailureAt;
                if (elapsed >= CIRCUIT_BREAKER_RESET_TIMEOUT_MS) {
                    breaker.state = "half-open";
                    breaker.halfOpenSuccesses = 0;
                    logger.debug("⚡ Circuit breaker half-open", { subscriber: subscriberName });
                    return true;
                }
                return false;
            }
            case "half-open":
                return true;
            default:
                return true;
        }
    }
    private recordCircuitBreakerSuccess(subscriberName: string): void {
        const breaker = this.circuitBreakers.get(subscriberName);
        if (!breaker)
            return;
        if (breaker.state === "half-open") {
            breaker.halfOpenSuccesses++;
            if (breaker.halfOpenSuccesses >= CIRCUIT_BREAKER_HALF_OPEN_SUCCESS_THRESHOLD) {
                breaker.state = "closed";
                breaker.consecutiveFailures = 0;
                breaker.halfOpenSuccesses = 0;
                logger.debug("⚡ Circuit breaker closed (recovered)", { subscriber: subscriberName });
            }
        }
        else if (breaker.state === "closed") {
            breaker.consecutiveFailures = 0;
        }
    }
    private recordCircuitBreakerFailure(subscriberName: string): void {
        const breaker = this.circuitBreakers.get(subscriberName);
        if (!breaker)
            return;
        breaker.consecutiveFailures++;
        breaker.lastFailureAt = Date.now();
        if (breaker.state === "half-open") {
            // Any failure in half-open re-opens the breaker
            breaker.state = "open";
            this.stats.circuitBreakerTrips++;
            logger.warn("⚡ Circuit breaker re-opened (half-open failure)", { subscriber: subscriberName });
        }
        else if (breaker.consecutiveFailures >= CIRCUIT_BREAKER_FAILURE_THRESHOLD) {
            breaker.state = "open";
            this.stats.circuitBreakerTrips++;
            logger.warn("⚡ Circuit breaker opened", {
                subscriber: subscriberName,
                consecutiveFailures: breaker.consecutiveFailures,
            });
        }
    }
    // ── Rate Limiting ────────────────────────────────────────────────────
    private checkRateLimit(subscriberName: string): boolean {
        const limiter = this.rateLimiters.get(subscriberName);
        if (!limiter)
            return true; // No limiter = allow
        const subscriber = this.subscribers.find(s => s.name === subscriberName);
        const maxPerSecond = subscriber?.maxEventsPerSecond ?? 0;
        if (maxPerSecond <= 0)
            return true;
        // Refill tokens based on elapsed time
        const now = Date.now();
        const elapsed = now - limiter.lastRefillAt;
        const tokensToAdd = (elapsed / 1000) * maxPerSecond;
        limiter.tokens = Math.min(maxPerSecond, limiter.tokens + tokensToAdd);
        limiter.lastRefillAt = now;
        if (limiter.tokens >= 1) {
            limiter.tokens -= 1;
            return true;
        }
        return false;
    }
    // ── Dead Letter Queue ────────────────────────────────────────────────
    private addToDeadLetterQueue(event: MyceliumEvent, error: string, subscriberName: string): void {
        // Check if this event+subscriber combo already exists
        const existing = this.deadLetterQueue.find(e => e.event.id === event.id && e.subscriberName === subscriberName);
        if (existing) {
            existing.attempts++;
            existing.lastFailedAt = Date.now();
            existing.error = error;
        }
        else {
            // Cap DLQ size to prevent unbounded memory growth
            if (this.deadLetterQueue.length >= DLQ_MAX_SIZE) {
                this.deadLetterQueue.shift(); // Remove oldest
            }
            this.deadLetterQueue.push({
                event,
                error,
                subscriberName,
                attempts: 1,
                firstFailedAt: Date.now(),
                lastFailedAt: Date.now(),
            });
        }
        this.stats.deadLetterCount = this.deadLetterQueue.length;
    }
    private updateAverageProcessingTime(newTime: number): void {
        const total = this.stats.totalEventsProcessed;
        const currentAvg = this.stats.averageProcessingTime;
        this.stats.averageProcessingTime = ((currentAvg * (total - 1)) + newTime) / total;
    }
}
// ============================================================================
// SINGLETON INSTANCE
// ============================================================================
export const myceliumEventBackbone = new MyceliumEventBackbone();
